import { API_URL } from '@/config';
import { gameLogger } from '@/utils/GameLogger';

export class ApiClient {
  private static async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_URL}${endpoint}`;
    gameLogger.info('network', `🌐 API Request: ${options.method || 'GET'} ${url}`, { body: options.body ? JSON.parse(options.body as string) : undefined });

    try {
      const res = await fetch(url, options);

      let data;
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      } else {
        data = await res.text();
      }

      gameLogger.info('network', `📥 API Response: ${res.status}`, { status: res.status, data });

      if (!res.ok) {
        const errorMessage = data.error?.message || data.error || data.message;
        const error = new Error(typeof errorMessage === 'string' ? errorMessage : `Server Error: ${res.status}`) as Error & { status?: number };
        error.status = res.status;
        throw error;
      }

      return data;
    } catch (err: any) {
      gameLogger.error('network', 'API Error', { error: err.message, endpoint });

      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        throw new Error('Server Unreachable. Is the server running?');
      }

      throw err;
    }
  }

  static async get(endpoint: string, token?: string | null) {
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return this.request(endpoint, {
      method: 'GET',
      headers
    });
  }

  static async post(endpoint: string, body: any, token?: string | null) {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return this.request(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
  }

  static async fetchCharacters(token: string) {
    return this.get('/characters', token);
  }

  static async createCharacter(token: string, data: { name: string, class: string, appearance: any, pin: string }) {
    return this.post('/characters', data, token);
  }

  /** Abandon (delete) a character the player owns. */
  static async deleteCharacter(token: string, characterId: string) {
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return this.request(`/characters/${encodeURIComponent(characterId)}`, {
      method: 'DELETE',
      headers,
    });
  }
}
